import React from 'react';
import Icon from '../../../components/AppIcon';

const ConfirmationRecordsList = ({ confirmations }) => {
  const formatCurrency = (amount) => {
    return `K${parseFloat(amount || 0)?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date?.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const formatTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date?.toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  };

  const getFilingTypeLabel = (type) => {
    switch (type) {
      case 'nasfund':
        return 'NASFUND Contribution';
      case 'swt':
        return 'SWT Filing';
      case 'annual_reconciliation':
        return 'Annual Reconciliation';
      default:
        return type;
    }
  };

  const getComplianceStatusColor = (status) => {
    switch (status) {
      case 'compliant':
        return 'var(--color-success)';
      case 'non_compliant':
        return 'var(--color-error)';
      case 'under_review':
        return 'var(--color-warning)';
      default:
        return 'var(--color-muted-foreground)';
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg p-4">
      <div className="flex items-center gap-2 mb-4">
        <Icon name="ShieldCheck" size={20} color="var(--color-success)" />
        <h3 className="font-heading font-semibold text-base md:text-lg text-foreground">
          Compliance Confirmation Records
        </h3>
      </div>
      <div className="space-y-3">
        {confirmations?.length === 0 ? (
          <div className="text-center py-8">
            <Icon name="Inbox" size={32} color="var(--color-muted-foreground)" className="mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">No confirmation records found</p>
          </div>
        ) : (
          confirmations?.map((confirmation) => (
            <div key={confirmation?.id} className="border border-border rounded-md p-3 transition-smooth hover:bg-muted/50">
              <div className="flex items-start gap-3">
                <div className="mt-1">
                  <Icon name="CheckCircle2" size={18} color="var(--color-success)" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="flex-1">
                      <h4 className="font-body font-medium text-sm text-foreground mb-1">
                        {confirmation?.lodgment ? getFilingTypeLabel(confirmation?.lodgment?.filingType) : 'Filing Confirmation'}
                      </h4>
                      {confirmation?.lodgment && (
                        <p className="text-xs text-muted-foreground">
                          Period: {confirmation?.lodgment?.filingPeriod} • Amount: {formatCurrency(confirmation?.lodgment?.totalAmount)}
                        </p>
                      )}
                    </div>
                    <div 
                      className="px-2 py-1 rounded text-xs font-medium"
                      style={{ 
                        backgroundColor: `${getComplianceStatusColor(confirmation?.complianceStatus)}15`,
                        color: getComplianceStatusColor(confirmation?.complianceStatus)
                      }}
                    >
                      {confirmation?.complianceStatus || 'Compliant'}
                    </div>
                  </div>
                  
                  <div className="bg-muted/50 rounded-md p-2 mb-2">
                    <div className="flex items-center gap-2">
                      <Icon name="Hash" size={14} color="var(--color-muted-foreground)" />
                      <span className="text-xs caption text-muted-foreground">Confirmation:</span>
                      <span className="text-xs data-text font-medium text-foreground font-mono">
                        {confirmation?.confirmationNumber}
                      </span>
                    </div>
                  </div>

                  {confirmation?.verificationNotes && (
                    <p className="text-xs text-muted-foreground mb-2">
                      {confirmation?.verificationNotes}
                    </p>
                  )}

                  <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                    <div className="flex items-center gap-1">
                      <Icon name="Calendar" size={12} color="currentColor" />
                      <span className="data-text">{formatDate(confirmation?.confirmationDate)}</span>
                    </div>
                    <span>•</span>
                    <div className="flex items-center gap-1">
                      <Icon name="Clock" size={12} color="currentColor" />
                      <span className="data-text">{formatTime(confirmation?.confirmationDate)}</span>
                    </div>
                    {confirmation?.verifiedBy && (
                      <>
                        <span>•</span>
                        <div className="flex items-center gap-1">
                          <Icon name="User" size={12} color="currentColor" />
                          <span>{confirmation?.verifiedBy}</span>
                        </div>
                      </>
                    )}
                  </div>

                  {confirmation?.confirmationDocumentUrl && (
                    <div className="mt-2">
                      <a
                        href={confirmation?.confirmationDocumentUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                      >
                        <Icon name="Download" size={12} color="currentColor" />
                        Download Confirmation Document
                      </a>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default ConfirmationRecordsList;
